import * as React from "react";
import { GenderValue } from "shared";
import { ConversationQuestionType as QTypes } from "fe-common/graphql";
import {
  capitalizeFirstLetter,
  symptomQualifierTxt,
  symptomTxt,
} from "../uiControllers/SymptomText";
import { getSuggestionAnswer } from "./getSuggestionAnswer";
import {
  getFreeTextFromQF,
  isFreeTextSuggestion,
} from "fe-common/logic/qualifiedFeature/freeText";
import { getFormAnswerText } from "../forms/getFormAnswerText";
import { AnswerElementExchange } from "./AnswerElementExchange";

const noAnswer = (exchange: AnswerElementExchange) =>
  exchange.answer?.qfs?.length === 0 ||
  (exchange.answer?.qfs?.length === 1 && exchange.answer?.qfs[0].negate);

const skippedAnswer = (exchange: AnswerElementExchange) =>
  exchange.answer?.qfs?.length === 0 ||
  (exchange.answer?.qfs?.length === 1 && exchange.answer.qfs[0].skip);

//region [[ Functions ]]

export type AnswerText = {
  text: React.ReactNode;
  isNumeric?: boolean;
  keepLtr?: boolean;
  isLongFormat?: boolean;
  /***
   * We use this string to pre-fix what this answer is answering in form questions
   */
  multiQuestionPrefix?: string;
};

export function getAnswerTextByType(
  translate: (id: string) => string,
  exchange: AnswerElementExchange,
  gender: GenderValue,
  isForm: boolean = false,
): AnswerText {
  const ANSWER_NO = translate("ANSWER_NO");

  const ANSWER_SKIP = translate("ANSWER_SKIP");

  if (!exchange) return;

  if (noAnswer(exchange)) return { text: ANSWER_NO };

  if (
    skippedAnswer(exchange) &&
    exchange.type !== QTypes.Suggestion &&
    exchange.type !== QTypes.BackgroundSuggestion &&
    exchange.type !== QTypes.Pregnancy &&
    exchange.type !== QTypes.FreeText
  ) {
    return { text: ANSWER_SKIP };
  }

  const selectedQfs = exchange.answer.qfs
    ?.filter((qf) => !qf.negate)
    ?.filter((qf) => !qf.skip);

  const commaSeparatesSymptoms =
    selectedQfs?.length > 1
      ? selectedQfs
          .map((qf) => symptomTxt(qf).join(""))
          .join(", ")
          .replace(/\*([^*]+?)\*/g, "<b>$1</b>")
      : selectedQfs?.length === 1
      ? symptomTxt(selectedQfs[0], isForm)
          .join("")
          .replace(/\*([^*]+?)\*/g, "<b>$1</b>")
      : "";

  switch (exchange.type) {
    // TODO: make it use the same as the numeric
    case QTypes.Age:
      return {
        text:
          (isForm ? exchange.answer.qfs[0].feature.name + ": <b>" : "") +
          exchange.answer.qfs[0].qualifiers[0].range.low.toString() +
          (isForm ? "</b>" : ""),
      };

    case QTypes.Gender:
      const genderText = gender
        ? gender === GenderValue.MALE
          ? translate("MALE")
          : translate("FEMALE")
        : "";
      return {
        text:
          (isForm ? exchange.answer.qfs[0].feature.name + ": <b>" : "") +
          genderText +
          (isForm ? "</b>" : ""),
      };
    case QTypes.Pregnancy: {
      if (selectedQfs.length) {
        return getSuggestionAnswer({
          selectedQfs,
          gender,
          question: exchange.question,
          translate,
        });
      } else {
        if (exchange.answer.qfs[0].negate)
          return { text: translate("NOT_PREGNANT_SURE") };
      }
      break;
    }

    case QTypes.BackgroundSuggestion:
    case QTypes.Suggestion: {
      if (isFreeTextSuggestion(exchange.question.suggestions[0])) {
        return {
          text: capitalizeFirstLetter([
            getFreeTextFromQF(exchange.answer.qfs?.[0]),
          ]),
          isLongFormat: true,
        };
      }

      return getSuggestionAnswer({
        selectedQfs: selectedQfs,
        gender,
        question: exchange.question,
        translate,
      });
    }
    case QTypes.Duration: {
      //for backward compatability - for old restored conversations
      //Until we decide what to do with translations of units
      const durationText = symptomQualifierTxt(selectedQfs[0]).join("");
      const [num, unit] = durationText.trim().split(" ");
      const answerText = num + " " + translate(unit.toLowerCase());
      return { text: answerText };
    }
    case QTypes.OtherComplaint:
      return { text: commaSeparatesSymptoms };
    case QTypes.FreeText:
      return {
        text: getFreeTextFromQF(exchange.answer.qfs?.[0]),
        isLongFormat: true,
      };
    case QTypes.Form:
      return getFormAnswerText({ translate, exchange, gender });
    case QTypes.MultipleChiefComplaint: {
      const text =
        selectedQfs?.length > 1
          ? selectedQfs
              .map((qf) => {
                let answerText = symptomTxt({ ...qf, qualifiers: [] }).join("");
                const durationText = symptomQualifierTxt(qf).join("");
                if (durationText !== "") {
                  const [num, unit] = durationText.trim().split(" ");
                  answerText =
                    answerText +
                    " " +
                    num +
                    " " +
                    translate(unit.toLowerCase());
                }
                return answerText;
              })
              .join(", ")
              .replace(/\*([^*]+?)\*/g, "<b>$1</b>")
          : selectedQfs?.length === 1
          ? (() => {
              let answerText = symptomTxt(
                { ...selectedQfs[0], qualifiers: [] },
                isForm,
              ).join("");
              const durationText = symptomQualifierTxt(selectedQfs[0]).join("");
              if (durationText !== "") {
                const [num, unit] = durationText.trim().split(" ");
                answerText =
                  answerText + " " + num + " " + translate(unit.toLowerCase());
              }
              return answerText.replace(/\*([^*]+?)\*/g, "<b>$1</b>");
            })()
          : "";

      return { text };
    }
    default:
      return {
        text: commaSeparatesSymptoms
          ? capitalizeFirstLetter([commaSeparatesSymptoms])
          : translate("ANSWER_NO"),
      };
  }
}
